import { useEffect, useRef } from 'react';
import { PageLoader } from '@customerio/pluma-components/react';
import { useAuthQuery } from '@/services/auth';
import { hydraUiApi } from '@/services/api';
import { isEmptyString } from '@/utils/string';
import { Navigate } from '@/utils/navigate';
import { hardRedirect } from '@/utils/hard-redirect';

/**
 * Academy Instant Auth redirect (SPE-2515). Authenticated users are redirected
 * to Academy via WorkRamp Instant Auth; unauthenticated users go to login and
 * are sent back here afterwards. Ported from the Ember `academy-login` route.
 */
export default function AcademyLoginPage() {
  const { data: auth, isLoading } = useAuthQuery();
  const accountId = auth?.account?.id;
  const userId = auth?.user?.id;
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (accountId == null || userId == null || hasRedirected.current) {
      return;
    }
    hasRedirected.current = true;
    void (async () => {
      try {
        // The WorkRamp auth_url endpoint is keyed by `dest`; this route always
        // targets Academy (matches the help sidebar's academy usage).
        const { url } = await hydraUiApi.users.getWorkrampAuthUrl({
          params: { path: { account_id: String(accountId), id: String(userId) }, query: { dest: 'academy' } },
        });
        // A blank URL would reload the page rather than navigate to Academy;
        // fall back to the app home like the error path does.
        hardRedirect(isEmptyString(url) ? '/' : url);
      } catch {
        // Mirror the Ember route's error handler: fall back to the app home.
        hardRedirect('/');
      }
    })();
  }, [accountId, userId]);

  if (isLoading) {
    return <PageLoader label="Loading…" />;
  }

  if (auth == null || auth.account == null || auth.user == null) {
    return <Navigate to="/login" saveCurrentUrl={true} />;
  }

  return <PageLoader label="Redirecting to Academy…" />;
}
